import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useIsSuperadmin } from "@/hooks/useUserRole";
import { useCurrentCompany } from "@/hooks/useCurrentCompany";

/**
 * Single gate for Settings › Company: superadmin or
 * company admin (user_companies.role='admin') of the ACTIVE company.
 * Used by both the route guard and the settings sidebar.
 */
export function useCanManageCompany() {
  const { user, isLoading: authLoading } = useAuth();
  const { isSuperadmin, isLoading: superLoading, error: superError, refetch: superRefetch } = useIsSuperadmin();
  const { id: companyId, loading: companyLoading } = useCurrentCompany();
  const [isCompanyAdmin, setIsCompanyAdmin] = useState(false);
  const [caLoading, setCaLoading] = useState(true);

  useEffect(() => {
    if (authLoading || companyLoading) { setCaLoading(true); return; }
    if (!user || !companyId) { setIsCompanyAdmin(false); setCaLoading(false); return; }
    let cancelled = false;
    setCaLoading(true);
    supabase
      .rpc("is_company_admin", { _user_id: user.id, _company_id: companyId })
      .then(({ data }) => {
        if (cancelled) return;
        setIsCompanyAdmin(data === true);
        setCaLoading(false);
      });
    return () => { cancelled = true; };
  }, [user, authLoading, companyId, companyLoading]);

  const allowed = isSuperadmin || isCompanyAdmin;
  return {
    allowed,
    isCompanyAdmin,
    isSuperadmin,
    isLoading: authLoading || superLoading || caLoading,
    error: superError,
    refetch: () => { superRefetch(); },
  };
}
